import Vue from 'vue';
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from 'vue-router';
import { Store } from 'vuex';

import { CollectionType } from '@/enums/collection-type';
import type { WebLocale } from '@/enums/web-locale';
import { Domain } from '@/routing/helpers/subdomain-helper';

import { HomePage, RentSignin } from '@/lazy-load-components';
import DachCampaign from '@/pages/landing-pages/campaigns/DachCampaign.vue';

import { PageType, makeAppRoutes } from '@/routing/config';
import { makeEditorialRoutes } from '@/routing/editorial';
import { makeUserRoutes } from '@/features/user-profile/routes';
import { makeSportRoutes } from '@/routing/sports';
import { makeRedirectRoutes } from '@/routing/redirect';
import { makeStaticRoutes } from '@/routing/static';
import { makeRentalListRoutes } from '@/routing/lists';

Vue.use(VueRouter);

function createRoutes(webLocale: WebLocale, store: Store<any>, currentDomain: Domain = Domain.MAIN): RouteConfig[] {
	// Build all route groups
	const homeRoutes = makeHomeRoute(currentDomain, store);
	const rentalRoutes = makeRentalListRoutes(webLocale);
	const sportsRoutes = makeSportRoutes(webLocale, store);
	const editorialRoutes = makeEditorialRoutes(webLocale);
	const appRoutes = makeAppRoutes(webLocale, store, currentDomain);
	const redirectRoutes = makeRedirectRoutes(webLocale);
	const userRoutes = makeUserRoutes(webLocale);
	const staticRoutes = makeStaticRoutes(webLocale, store);

	const campaignRoutes: RouteConfig[] = [
		{
			name: 'app.campaign.dach',
			path: '/dach/campaign',
			component: DachCampaign,
		},
		{
			name: 'app.rent.signin',
			path: '/rent/signin',
			component: RentSignin,
		},
	];

	// Order routes by priority based on current domain
	// Current subdomain's routes MUST come first to match before cross-domain routes with same paths
	let prioritizedRoutes: RouteConfig[];

	if (currentDomain === Domain.SPORTS) {
		prioritizedRoutes = [
			...sportsRoutes,
			...homeRoutes,
			...rentalRoutes,
			...appRoutes,
			...redirectRoutes,
			...userRoutes,
			...campaignRoutes,
			...staticRoutes,
			...editorialRoutes,
		];
	} else if (currentDomain === Domain.EDITORIAL) {
		prioritizedRoutes = [
			...editorialRoutes,
			...homeRoutes,
			...rentalRoutes,
			...appRoutes,
			...redirectRoutes,
			...userRoutes,
			...campaignRoutes,
			...staticRoutes,
			...sportsRoutes,
		];
	} else {
		prioritizedRoutes = [
			...homeRoutes,
			...rentalRoutes,
			...appRoutes,
			...redirectRoutes,
			...userRoutes,
			...campaignRoutes,
			...staticRoutes,
			...sportsRoutes,
			...editorialRoutes,
		];
	}

	// Return all routes - domain switching is handled in navigation guards
	return prioritizedRoutes;
}

function makeHomeRoute(currentDomain: Domain, store: Store<any>) {
	if (currentDomain === Domain.MAIN) {
		return [
			{
				name: 'app.home',
				path: '/',
				component: HomePage,
				beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext<Vue>) => {
					await store.dispatch('language/fetchSportsTranslation');
					next();
				},
				meta: {
					collectionType: CollectionType.GLOBAL,
					hasFilterBar: false,
					initialRouteState: {},
					contexts: [],
					tab: 'home',
					pageType: PageType.HOME,
				},
			},
		];
	} else {
		return [];
	}
}

function createRouter(webLocale: WebLocale, store: Store<any>, currentDomain: Domain = Domain.MAIN) {
	const router = new VueRouter({
		mode: 'history',
		base: process.env.BASE_URL,
		routes: createRoutes(webLocale, store, currentDomain),
		scrollBehavior(to, _from, savedPosition) {
			// the sports tab has several children pages that should keep scroll position independently.
			// each sports route has a meta.data identifier which will be used to keep the scroll position distinct for each child page.
			const tab = to?.meta?.tab === 'sports' ? `${to?.meta?.tab}-${to?.meta?.data}` : to?.meta?.tab;

			// sports event is pretty much the equivalent to the detail page.
			// thus its scroll position needs to be reset every time it gets looked up.
			if (tab === 'detail' || tab === 'editorial' || to?.name === 'app.sports.subdomain.event') {
				return { x: 0, y: 0 };
			}

			const { scrollPositions } = store.state.routing;
			if (scrollPositions[tab]) {
				return { x: 0, y: scrollPositions[tab] };
			}

			if (savedPosition) {
				return savedPosition;
			}

			return { x: 0, y: 0 };
		},
	});

	// Store the current domain for use in changeLocale
	(router as any)._currentDomain = currentDomain;

	return router;
}

VueRouter.prototype.changeLocale = async function (locale: WebLocale, store: Store<any>) {
	// Preserve the current domain when changing locale
	const currentDomain = (this as any)._currentDomain || Domain.MAIN;
	this.matcher = createRouter(locale, store, currentDomain).matcher;
};

export { createRouter };
